import React, { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';


// 1) Polyfill Promise.withResolvers
if (typeof Promise !== "undefined" && typeof Promise.withResolvers === "undefined") {
  Promise.withResolvers = function () {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// 确保与项目中的 pdfjs-dist 版本一致的 worker 路径
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/js/pdf.worker.min.js`;

// 工具：仅当传入的是 dataURL 时，转换为 Uint8Array（加密场景更稳）
function toUint8IfDataUrl(file) {
  if (!file) return null;
  if (typeof file === 'string' && file.startsWith('data:')) {
    const comma = file.indexOf(',');
    const base64 = file.slice(comma + 1);
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { data: bytes }; // react-pdf 接受 { data: Uint8Array }
  }
  return file; // URL/Blob/{data,password} 直接返回
}

/**
 * 用法：
 * <PdfThumbnail base64={doc.orgUrl} size={48} />
 * - 若文档加密：展示占位图，不渲染缩略图；点击占位图在新标签页打开原文。
 * - 若文档未加密：渲染第一页缩略图。
 */
const PdfThumbnail = ({ base64, size = 48, style, className }) => {
  const [isEncrypted, setIsEncrypted] = useState(false); // 一旦需要密码则认为加密
  const [fallback, setFallback] = useState(false);        // 其他错误兜底（损坏、CORS等）

  // 仅当 base64 变化时做一次转换，保持 file 引用稳定，避免不必要的重载
  const normalizedFile = useMemo(() => toUint8IfDataUrl(base64), [base64]);

  const handlePassword = (callback, reason) => {
    // 1=NeedPassword, 2=IncorrectPassword
    // 缩略图场景不要求输入密码，直接标记加密并走占位图，且不调用 callback，避免系统 prompt
    setIsEncrypted(true);
    setFallback(false);
    // ⚠️ 不调用 callback，缩略图不再尝试解密
  };

  const handleLoadError = (err) => {
    // 任何异常（包含密码异常、文件损坏、跨域问题）都走占位图
    console.error('PDF 缩略图加载失败：', err);
    setFallback(true);
  };

  // 点击占位图/缩略图时在新标签页打开原文
  const openInNewTab = () => {
    if (!base64) return;
    // base64 是 dataURL 时可以直接打开；如果是 URL/Blob，按需适配
    window.open(base64, '_blank', 'noopener,noreferrer');
  };

  const fallbackIcon = (
    <div
      onClick={openInNewTab}
      title="打开新标签页预览"
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #eee',
        borderRadius: 6,
        cursor: base64 ? 'pointer' : 'default',
        background: '#fafafa',
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL}/images/document-doc.svg`}
        alt="doc"
        style={{ width: size - 6, height: size - 6, objectFit: 'cover', opacity: 0.9 }}
      />
    </div>
  );

  // 如果已识别为加密或发生错误，直接展示占位，不渲染 Page
  if (isEncrypted || fallback || !normalizedFile) {
    return (
      <div
        className={className}
        style={{ width: size, height: size, ...style }}
      >
        {fallbackIcon}
      </div>
    );
  }

  // 未加密：渲染第一页作为缩略图
  return (
    <div
      className={className}
      style={{ width: size, height: size, ...style }}
      // 也可以允许点击缩略图在新标签页打开（按需保留/移除）
      onClick={openInNewTab}
      title="打开新标签页预览"
    >
      <Document
        file={normalizedFile}
        onLoadSuccess={() => { /* 缩略图无需页数 */ }}
        onLoadError={handleLoadError}
        onPassword={handlePassword}
        loading={<div style={{ width: size, height: size, fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>loading…</div>}
        error={fallbackIcon}
      >
        <Page pageNumber={1} width={size} height={size} />
      </Document>
    </div>
  );
};

export default PdfThumbnail;
